/**
 * `/content/assignments` page: URL ⇄ store filter sync, first load, timezone refetch. The sync lives here
 * rather than in a route middleware so back/forward through filter history can refetch.
 */
import type { LocationQuery } from '#vue-router'

import { useOnTimezoneChange, useSidebarPrivileges } from '@entities/viewer'
import { MAX_PER_PAGE, getFirstQueryValue, toIdValue, toPaginationValue } from '@shared/lib/routes'

import { AssignmentType, Grade } from './enums'
import { useAssignmentTemplatesStore } from '../assignment-templates/store'
import { useAssignmentTemplatesApi } from '../../api/assignment-templates/list.hook'
import { DISCIPLINE_CODES } from './assignment-template-options'

interface AssignmentsListReturn {
  refCanView: ComputedRef<boolean>
}

/** Membership-checked, not cast: `?type=lol` lands as `null`. */
const toEnumValue = <T extends string>(value: unknown, allowed: readonly T[]): T | null => {
  const raw = getFirstQueryValue(value)

  return allowed.find(item => item === raw) ?? null
}

export const useAssignmentsList = (): AssignmentsListReturn => {
  const route = useRoute()
  const store = useAssignmentTemplatesStore()
  const { canViewAssignmentTemplates } = useSidebarPrivileges()

  /** Query → store; returns whether anything changed. */
  const applyQueryToStore = (query: LocationQuery): boolean => {
    const search = getFirstQueryValue(query?.search) || null
    const type = toEnumValue<AssignmentType>(query?.type, Object.values(AssignmentType))
    const disciplineCode = toEnumValue(query?.discipline_code, DISCIPLINE_CODES)
    const grade = toEnumValue<Grade>(query?.grade, Object.values(Grade))
    // A module only means something inside a grade + discipline; a bare id is dropped.
    const moduleId = grade && disciplineCode ? toIdValue(query?.curriculum_module_id) : null
    const page = toPaginationValue(query?.page, 1)
    const perPage = toPaginationValue(query?.per_page, store.getPerPage, MAX_PER_PAGE)

    const isSame = search === store.getFilterSearch
      && type === store.getFilterType
      && disciplineCode === store.getFilterDisciplineCode
      && grade === store.getFilterGrade
      && moduleId === store.getFilterModuleId
      && page === store.getCurrentPage
      && perPage === store.getPerPage

    store.setFilterSearch(search)
    store.setFilterType(type)
    store.setFilterDisciplineCode(disciplineCode)
    store.setFilterGrade(grade)
    store.setFilterModuleId(moduleId)
    store.setCurrentPage(page)
    store.setPerPage(perPage)

    return !isSame
  }

  const refCanView = computed<boolean>(() => canViewAssignmentTemplates.value)

  applyQueryToStore(route.query)

  onMounted(() => {
    if (!refCanView.value) return

    void useAssignmentTemplatesApi()
  })

  // Timestamps arrive pre-shifted into the admin's view timezone, so a change needs a refetch.
  useOnTimezoneChange(() => {
    if (!refCanView.value) return

    void useAssignmentTemplatesApi()
  })

  // A filter's own push is a no-op here; only a real back/forward refetches.
  watch(() => route.query, (query) => {
    if (!applyQueryToStore(query) || !refCanView.value) return

    void useAssignmentTemplatesApi()
  })

  return {
    refCanView,
  }
}
